import { useState, useEffect, useCallback } from "react"
import styles from "./finance-page.module.css"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import { confirmDeposit } from "@/lib/supabase/rpc"
import { LoadingSkeleton } from "@/client/components/common/loading-skeleton"
import { useAdminStore } from "@/shared/hooks/use-admin-store"
import type { PointTransaction, DepositRequest, Bounty } from "@/lib/supabase/types"

interface WithdrawRecord {
  id: string
  user: string
  avatar: string
  userId: string
  amount: number
  bankName: string
  bankAccount: string
  applyTime: string
  status: "pending" | "approved" | "rejected"
}

interface OrderRecord {
  id: string
  user: string
  type: string
  amount: number
  time: string
  status: "completed" | "pending" | "refunded"
}

interface ChartData {
  day: string
  revenue: number
}

const PAGE_SIZE = 15

export default function FinancePage() {
  const { admin } = useAdminStore()
  const [activeTab, setActiveTab] = useState<"overview" | "withdraw" | "orders">("overview")
  const [dateRange, setDateRange] = useState<"week" | "month" | "quarter">("week")
  const [searchKeyword, setSearchKeyword] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [withdrawList, setWithdrawList] = useState<WithdrawRecord[]>([])
  const [orderList, setOrderList] = useState<OrderRecord[]>([])
  const [stats, setStats] = useState({
    today: 0,
    week: 0,
    month: 0,
    pending: 0,
  })
  const [chartData, setChartData] = useState<Record<string, ChartData[]>>({
    week: [],
    month: [],
    quarter: [],
  })
  const [topSources, setTopSources] = useState<{ source: string; amount: number }[]>([])

  // 获取财务数据
  const fetchFinanceData = useCallback(async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    setIsLoading(true)
    try {
      // 获取今日收入（平台手续费 + 积分消费）
      const today = new Date().toISOString().split("T")[0]
      const { data: todayTransactions } = await supabase
        .from("point_transactions")
        .select("amount, type")
        .gte("created_at", today)
        .in("type", ["platform_fee", "consume"])

      const todayRevenue = (todayTransactions || []).reduce((sum, t) => sum + Math.abs(t.amount), 0)

      // 获取本周收入
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      const { data: weekTransactions } = await supabase
        .from("point_transactions")
        .select("amount, type")
        .gte("created_at", weekAgo)
        .in("type", ["platform_fee", "consume"])

      const weekRevenue = (weekTransactions || []).reduce((sum, t) => sum + Math.abs(t.amount), 0)

      // 获取本月收入
      const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      const { data: monthTransactions } = await supabase
        .from("point_transactions")
        .select("amount, type")
        .gte("created_at", monthAgo)
        .in("type", ["platform_fee", "consume"])

      const monthRevenue = (monthTransactions || []).reduce((sum, t) => sum + Math.abs(t.amount), 0)

      // 获取待提现金额（提现申请中的金额）
      const { data: pendingWithdrawals } = await supabase
        .from("deposit_requests")
        .select("amount_bbp")
        .eq("status", "pending")

      const pendingAmount = (pendingWithdrawals || []).reduce((sum, w) => sum + w.amount_bbp, 0)

      // 获取提现记录
      const { data: deposits } = await supabase
        .from("deposit_requests")
        .select(`
          id,
          amount_bbp,
          status,
          created_at,
          user:users!deposit_requests_user_id_fkey (nickname, id)
        `)
        .order("created_at", { ascending: false })
        .limit(50)

      const mappedWithdrawals: WithdrawRecord[] = (deposits || []).map((d: any) => ({
        id: d.id,
        user: d.user?.nickname || "未知用户",
        avatar: (d.user?.nickname || "?")[0],
        userId: d.user?.id?.slice(0, 8) || "",
        amount: d.amount_bbp,
        bankName: "支付宝",
        bankAccount: d.user?.id ? `****${d.user.id.slice(-4)}` : "****",
        applyTime: new Date(d.created_at).toLocaleString("zh-CN"),
        status: d.status === "pending" ? "pending" : d.status === "confirmed" ? "approved" : "rejected",
      }))

      // 获取交易记录（积分交易）
      const { data: transactions } = await supabase
        .from("point_transactions")
        .select(`
          id,
          type,
          amount,
          status,
          created_at,
          user:users!point_transactions_user_id_fkey (nickname)
        `)
        .order("created_at", { ascending: false })
        .limit(50)

      const typeMap: Record<string, string> = {
        deposit: "充值",
        withdraw: "提现",
        earn: "任务奖励",
        consume: "积分消费",
        platform_fee: "平台手续费",
        refund: "退款",
        escrow_release: "托管释放",
      }

      const mappedOrders: OrderRecord[] = (transactions || []).map((t: any) => ({
        id: t.id.slice(0, 12).toUpperCase(),
        user: t.user?.nickname || "未知用户",
        type: typeMap[t.type] || t.type,
        amount: Math.abs(t.amount),
        time: new Date(t.created_at).toLocaleString("zh-CN"),
        status: t.status === "success" ? "completed" : t.status === "pending" ? "pending" : "refunded",
      }))

      // 获取图表数据
      await fetchChartData()

      // 获取收入来源统计
      await fetchTopSources()

      setStats({
        today: todayRevenue,
        week: weekRevenue,
        month: monthRevenue,
        pending: pendingAmount,
      })
      setWithdrawList(mappedWithdrawals)
      setOrderList(mappedOrders)
    } catch (error) {
      console.error("获取财务数据失败:", error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // 获取图表数据
  const fetchChartData = async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    const now = new Date()

    // 近7天数据
    const weekData: ChartData[] = []
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now)
      date.setDate(date.getDate() - i)
      const dayStr = date.toISOString().split("T")[0]
      const { data } = await supabase
        .from("point_transactions")
        .select("amount")
        .gte("created_at", dayStr)
        .lt("created_at", dayStr + "T23:59:59")
        .in("type", ["platform_fee", "consume"])
        .eq("status", "success")

      const revenue = (data || []).reduce((sum, t) => sum + Math.abs(t.amount), 0)
      weekData.push({
        day: ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][date.getDay()],
        revenue,
      })
    }

    // 近4周数据
    const monthData: ChartData[] = []
    for (let i = 3; i >= 0; i--) {
      const startDate = new Date(now)
      startDate.setDate(startDate.getDate() - (i + 1) * 7)
      const endDate = new Date(now)
      endDate.setDate(endDate.getDate() - i * 7)

      const { data } = await supabase
        .from("point_transactions")
        .select("amount")
        .gte("created_at", startDate.toISOString())
        .lt("created_at", endDate.toISOString())
        .in("type", ["platform_fee", "consume"])
        .eq("status", "success")

      const revenue = (data || []).reduce((sum, t) => sum + Math.abs(t.amount), 0)
      monthData.push({ day: `第${4 - i}周`, revenue })
    }

    // 近3个月数据
    const quarterData: ChartData[] = []
    for (let i = 2; i >= 0; i--) {
      const date = new Date(now)
      date.setMonth(date.getMonth() - i)
      const monthStr = date.toISOString().slice(0, 7)

      const { data } = await supabase
        .from("point_transactions")
        .select("amount")
        .gte("created_at", monthStr + "-01")
        .lt("created_at", monthStr + "-31")
        .in("type", ["platform_fee", "consume"])
        .eq("status", "success")

      const revenue = (data || []).reduce((sum, t) => sum + Math.abs(t.amount), 0)
      quarterData.push({ day: `${date.getMonth() + 1}月`, revenue })
    }

    setChartData({
      week: weekData,
      month: monthData,
      quarter: quarterData,
    })
  }

  // 获取收入来源统计
  const fetchTopSources = async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    const { data } = await supabase
      .from("point_transactions")
      .select("type, amount")
      .in("type", ["platform_fee", "earn", "consume"])
      .eq("status", "success")

    const sourceMap: Record<string, string> = {
      platform_fee: "平台手续费",
      earn: "任务完成",
      consume: "课程购买",
    }

    const amounts: Record<string, number> = {}
    ;(data || []).forEach((t) => {
      const name = sourceMap[t.type] || t.type
      amounts[name] = (amounts[name] || 0) + Math.abs(t.amount)
    })

    const sorted = Object.entries(amounts)
      .map(([source, amount]) => ({ source, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5)

    setTopSources(sorted)
  }

  useEffect(() => {
    fetchFinanceData()
  }, [fetchFinanceData])

  const currentChartData = chartData[dateRange]
  const maxRevenue = currentChartData.length > 0 ? Math.max(...currentChartData.map((d) => d.revenue)) : 1

  const handleApprove = async (id: string) => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase || !admin?.id) return

    try {
      await confirmDeposit(supabase, id, admin.id)

      setWithdrawList((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: "approved" as const } : item))
      )
    } catch {
      alert("批准失败，请重试")
    }
  }

  const handleReject = async (id: string) => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) return

    try {
      const { error } = await supabase
        .from("deposit_requests")
        .update({ status: "rejected", reviewed_at: new Date().toISOString() })
        .eq("id", id)

      if (error) throw error

      setWithdrawList((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: "rejected" as const } : item))
      )
    } catch {
      alert("拒绝失败，请重试")
    }
  }

  const filteredWithdrawList = withdrawList.filter(
    (item) =>
      item.user.includes(searchKeyword) ||
      item.userId.includes(searchKeyword)
  )

  const filteredOrderList = orderList.filter(
    (item) =>
      item.user.includes(searchKeyword) ||
      item.id.includes(searchKeyword) ||
      item.type.includes(searchKeyword)
  )

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>财务管理</h1>
        </div>
        <div className={styles.stats}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.statCard}>
              <LoadingSkeleton variant="rectangular" height={60} />
            </div>
          ))}
        </div>
        <div className={styles.content}>
          <LoadingSkeleton variant="rectangular" height={400} />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>财务管理</h1>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="搜索用户、订单号..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* 统计卡片 */}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <span className={styles.statIcon}>📈</span>
          <div className={styles.statContent}>
            <span className={styles.statValue}>¥{stats.today.toLocaleString()}</span>
            <span className={styles.statLabel}>今日收入</span>
          </div>
          <span className={styles.statTrend}>+12.5%</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statIcon}>📅</span>
          <div className={styles.statContent}>
            <span className={styles.statValue}>¥{stats.week.toLocaleString()}</span>
            <span className={styles.statLabel}>本周收入</span>
          </div>
          <span className={styles.statTrend}>+8.3%</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statIcon}>💰</span>
          <div className={styles.statContent}>
            <span className={styles.statValue}>¥{stats.month.toLocaleString()}</span>
            <span className={styles.statLabel}>本月收入</span>
          </div>
          <span className={styles.statTrend}>+15.2%</span>
        </div>
        <div className={`${styles.statCard} ${styles.pendingCard}`}>
          <span className={styles.statIcon}>⏳</span>
          <div className={styles.statContent}>
            <span className={`${styles.statValue} ${styles.pendingValue}`}>
              ¥{stats.pending.toLocaleString()}
            </span>
            <span className={styles.statLabel}>待提现金额</span>
          </div>
          <span className={styles.pendingBadge}>
            {withdrawList.filter((w) => w.status === "pending").length} 笔待处理
          </span>
        </div>
      </div>

      {/* 标签页切换 */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "overview" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <span className={styles.tabIcon}>📊</span>
          收入概览
        </button>
        <button
          className={`${styles.tab} ${activeTab === "withdraw" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("withdraw")}
        >
          <span className={styles.tabIcon}>💸</span>
          提现管理
          <span className={styles.badge}>
            {withdrawList.filter((w) => w.status === "pending").length}
          </span>
        </button>
        <button
          className={`${styles.tab} ${activeTab === "orders" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("orders")}
        >
          <span className={styles.tabIcon}>📋</span>
          订单流水
        </button>
      </div>

      {/* 内容区域 */}
      <div className={styles.content}>
        {activeTab === "overview" && (
          <div className={styles.overview}>
            <div className={styles.chartSection}>
              <div className={styles.chartHeader}>
                <h3>收入趋势</h3>
                <div className={styles.dateSelector}>
                  <button
                    className={`${styles.dateBtn} ${dateRange === "week" ? styles.activeDateBtn : ""}`}
                    onClick={() => setDateRange("week")}
                  >
                    近7天
                  </button>
                  <button
                    className={`${styles.dateBtn} ${dateRange === "month" ? styles.activeDateBtn : ""}`}
                    onClick={() => setDateRange("month")}
                  >
                    近30天
                  </button>
                  <button
                    className={`${styles.dateBtn} ${dateRange === "quarter" ? styles.activeDateBtn : ""}`}
                    onClick={() => setDateRange("quarter")}
                  >
                    近3个月
                  </button>
                </div>
              </div>
              <div className={styles.barChart}>
                {currentChartData.map((item) => (
                  <div key={item.day} className={styles.barItem}>
                    <div
                      className={styles.bar}
                      style={{ height: `${(item.revenue / maxRevenue) * 100}%` }}
                    >
                      <span className={styles.barTooltip}>¥{(item.revenue / 1000).toFixed(1)}k</span>
                    </div>
                    <span className={styles.barLabel}>{item.day}</span>
                  </div>
                ))}
              </div>
              <div className={styles.chartSummary}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>总收入</span>
                  <span className={styles.summaryValue}>
                    ¥{currentChartData.reduce((sum, d) => sum + d.revenue, 0).toLocaleString()}
                  </span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>日均</span>
                  <span className={styles.summaryValue}>
                    ¥{(currentChartData.reduce((sum, d) => sum + d.revenue, 0) / currentChartData.length || 0).toFixed(0)}
                  </span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>峰值</span>
                  <span className={styles.summaryValue}>¥{maxRevenue.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className={styles.topList}>
              <h3>收入来源 Top5</h3>
              <div className={styles.list}>
                {topSources.length === 0 ? (
                  <div className={styles.emptyState}>
                    <span className={styles.emptyText}>暂无数据</span>
                  </div>
                ) : (
                  topSources.map((item, index) => (
                    <div key={item.source} className={styles.listItem}>
                      <span className={`${styles.rank} ${index < 3 ? styles.topRank : ""}`}>
                        {index + 1}
                      </span>
                      <span className={styles.source}>{item.source}</span>
                      <div className={styles.sourceBar}>
                        <div
                          className={styles.sourceBarFill}
                          style={{ width: `${(item.amount / (topSources[0]?.amount || 1)) * 100}%` }}
                        />
                      </div>
                      <span className={styles.amount}>¥{item.amount.toLocaleString()}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "withdraw" && (
          <div className={styles.withdrawSection}>
            <div className={styles.withdrawHeader}>
              <div className={styles.withdrawTabs}>
                <button className={`${styles.withdrawTab} ${styles.activeWithdrawTab}`}>
                  待处理 ({withdrawList.filter((w) => w.status === "pending").length})
                </button>
                <button className={styles.withdrawTab}>
                  已通过 ({withdrawList.filter((w) => w.status === "approved").length})
                </button>
                <button className={styles.withdrawTab}>
                  已拒绝 ({withdrawList.filter((w) => w.status === "rejected").length})
                </button>
              </div>
            </div>
            <div className={styles.withdrawList}>
              {filteredWithdrawList.filter((w) => w.status === "pending").length === 0 ? (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>🎉</span>
                  <span className={styles.emptyText}>暂无待处理的提现申请</span>
                </div>
              ) : (
                filteredWithdrawList
                  .filter((w) => w.status === "pending")
                  .map((item) => (
                    <div key={item.id} className={styles.withdrawItem}>
                      <div className={styles.userInfo}>
                        <span className={styles.avatar}>{item.avatar}</span>
                        <div>
                          <span className={styles.userName}>{item.user}</span>
                          <span className={styles.userId}>ID: {item.userId}</span>
                        </div>
                      </div>
                      <div className={styles.bankInfo}>
                        <span className={styles.bankName}>{item.bankName}</span>
                        <span className={styles.bankAccount}>{item.bankAccount}</span>
                      </div>
                      <span className={styles.withdrawAmount}>
                        ¥{item.amount.toLocaleString()}
                      </span>
                      <span className={styles.withdrawTime}>{item.applyTime}</span>
                      <div className={styles.withdrawActions}>
                        <button
                          className={styles.approveBtn}
                          onClick={() => handleApprove(item.id)}
                        >
                          <span>✓</span> 通过
                        </button>
                        <button
                          className={styles.rejectBtn}
                          onClick={() => handleReject(item.id)}
                        >
                          <span>✕</span> 拒绝
                        </button>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        )}

        {activeTab === "orders" && (
          <div className={styles.ordersSection}>
            <div className={styles.ordersFilter}>
              <select className={styles.filterSelect}>
                <option value="">全部类型</option>
                <option value="course">课程购买</option>
                <option value="member">会员费</option>
                <option value="contest">赛事报名</option>
                <option value="task">任务完成</option>
                <option value="tip">打赏</option>
              </select>
              <select className={styles.filterSelect}>
                <option value="">全部状态</option>
                <option value="completed">已完成</option>
                <option value="pending">处理中</option>
                <option value="refunded">已退款</option>
              </select>
            </div>
            <div className={styles.orderList}>
              <div className={styles.orderHeader}>
                <span>订单号</span>
                <span>用户</span>
                <span>类型</span>
                <span>金额</span>
                <span>时间</span>
                <span>状态</span>
              </div>
              {filteredOrderList.length === 0 ? (
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}>📭</span>
                  <span className={styles.emptyText}>没有找到相关订单</span>
                </div>
              ) : (
                filteredOrderList.map((order) => (
                  <div key={order.id} className={styles.orderRow}>
                    <span className={styles.orderId}>{order.id}</span>
                    <span className={styles.orderUser}>{order.user}</span>
                    <span className={styles.orderType}>{order.type}</span>
                    <span className={styles.orderAmount}>
                      <span className={styles.currency}>¥</span>
                      {order.amount}
                    </span>
                    <span className={styles.orderTime}>{order.time}</span>
                    <span
                      className={`${styles.orderStatus} ${
                        order.status === "completed"
                          ? styles.statusCompleted
                          : order.status === "pending"
                          ? styles.statusPending
                          : styles.statusRefunded
                      }`}
                    >
                      {order.status === "completed"
                        ? "已完成"
                        : order.status === "pending"
                        ? "处理中"
                        : "已退款"}
                    </span>
                  </div>
                ))
              )}
            </div>
            <div className={styles.pagination}>
              <span className={styles.pageInfo}>共 {filteredOrderList.length} 条记录</span>
              <div className={styles.pageButtons}>
                <button className={styles.pageBtn} disabled>
                  上一页
                </button>
                <span className={styles.currentPage}>1 / 1</span>
                <button className={styles.pageBtn} disabled>
                  下一页
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
